import { CustomerService } from '@dynamik/common';
import { GlAccountTransaction, GlAccountTransactionsClient } from '@dynamik/common/clients/reporting-clients';
import {
  ActionType, AgGridFilterModel, AgGridPageRequest, GlTransactionInsight, GlTransactionInsightLine, GlTransactionsInsightClient,
  InstrumentTransaction
} from '@dynamik/common/clients/transaction-clients';
import { observable } from 'aurelia-binding';
import { bindable, customElement } from 'aurelia-framework';
import { InboxService } from '../../services/inbox-service';
import { DocumentsClient, DocumentState } from '@dynamik/common/clients/inboxv2-clients';

enum GroupMode {
  ByPrimaryTransaction = 1,
  ByAction = 2,
  None = 3
}

@customElement('voucher-graph')
export class VoucherGraph {
  constructor(private glTransactionsInsightClient: GlTransactionsInsightClient, private glAccountTransactionsClient: GlAccountTransactionsClient,
    private customerService: CustomerService, private documentsClient: DocumentsClient, private inboxService: InboxService) { }

  @bindable
  voucherNr: string;
  voucherNrChanged() {
    this.load();
  }

  loading = true;
  insightGlTransaction: GlTransactionInsight;
  transaction: InstrumentTransaction;
  groupedByTransaction: { transaction: InstrumentTransaction, lines: GlTransactionInsightLine[] }[];
  groupedByPrimary: { transactions: InstrumentTransaction[], lines: GlTransactionInsightLine[] }[];
  groupsCount: number;

  @observable
  groupMode: GroupMode = GroupMode.ByPrimaryTransaction;
  groupModeChanged() {
    this.updateGroupedLines();
  }

  glTransactions: GlAccountTransaction[] = undefined;
  document: DocumentState;
  glTransactionsDiv: HTMLDivElement = undefined;
  GroupMode = GroupMode;

  async load() {
    if (!this.voucherNr) {
      return;
    }
    const response = await this.glTransactionsInsightClient.getAll(this.customerService.selectedCustomer.companyId, new AgGridPageRequest({
      startRow: 0,
      endRow: 1,
      filterModel: {
        'transaction.voucherNr': new AgGridFilterModel({
          filterType: 'text',
          type: 'contains',
          filter: this.voucherNr
        })
      }
    }));
    this.insightGlTransaction = response.rowData[0];
    this.transaction = this.insightGlTransaction.glTransactionInsightLines[0]?.instrumentTransactions[0]?.instrumentTransaction;

    [this.document, this.glTransactions] = await Promise.all([
      this.documentsClient.get(this.customerService.selectedCustomer.companyId, this.transaction.documentId),
      this.insightGlTransaction.externalVoucherId ? await this.glAccountTransactionsClient.get(this.customerService.selectedCustomer.companyId, this.insightGlTransaction.externalVoucherId) : undefined
    ]);
    this.updateGroupedLines();
    this.loading = false;
  }

  async downloadDocument() {
    await this.inboxService.downloadDocument(this.transaction.documentId);
  }

  async downloadVoucher() {
    await this.inboxService.downloadVoucherDocument(this.transaction.voucherId);
  }

  updateGroupedLines() {
    if (!this.insightGlTransaction) {
      return;
    }
    switch (this.groupMode) {
      case GroupMode.ByPrimaryTransaction:
        this.groupedByPrimary = this.insightGlTransaction.glTransactionInsightLines.groupBy(x => this.getSourceId(x.instrumentTransactions[0].instrumentTransaction)).toArray()
          .map(x => ({
            transactions: x.value.map(y => y.instrumentTransactions[0].instrumentTransaction).distinctBy(x => x.id).sort((a, b) => a.id - b.id),
            lines: x.value
          }));
        this.groupsCount = this.groupedByPrimary.length;
        break;
      case GroupMode.ByAction:
        this.groupedByTransaction = this.insightGlTransaction.glTransactionInsightLines.groupBy(x => x.instrumentTransactions[0].instrumentTransaction.actionType).toArray()
          .map(x => ({
            transaction: new InstrumentTransaction({ actionType: x.key, amount: x.value.distinctBy(y => y.instrumentTransactions[0].instrumentTransactionId).sum(y => y.instrumentTransactions[0].instrumentTransaction.amount) }),
            lines: x.value.groupBy(y => y.accountNumber).toArray().map(y => new GlTransactionInsightLine({ accountNumber: y.key, amount: y.value.sum(z => z.amount), glAccountType: y.value[0].glAccountType }))
          }))
          .sort((a, b) => a.transaction.id - b.transaction.id);
        this.groupsCount = this.groupedByTransaction.length;
        break;
      case GroupMode.None:
        this.groupedByTransaction = this.insightGlTransaction.glTransactionInsightLines.groupBy(x => x.instrumentTransactions[0].instrumentTransactionId).toArray()
          .map(x => ({
            transaction: x.value[0].instrumentTransactions[0].instrumentTransaction,
            lines: x.value
          }))
          .sort((a, b) => a.transaction.id - b.transaction.id);
        this.groupsCount = this.groupedByTransaction.length;
        break;
    }
  }

  getSourceId(transaction: InstrumentTransaction) {
    return [ActionType.Sold, ActionType.Purchased, ActionType.ShortSale, ActionType.ShortPurchased].includes(transaction.actionType) ? transaction.id : transaction.parentTransactionsId;
  }
}
